/**
 * Extended Data Context for Zenda
 *
 * Provides access to CRUD operations for sprints, epics, task relations,
 * milestones, automations, and templates through a React context.
 * Follows the same patterns as DatabaseContext (useCallback, error handling).
 */

import React, { createContext, useContext, useCallback, useMemo, useRef, useState } from 'react';

// Sprint imports
import {
  getSprintsBySpace,
  getSprintSummaries,
  getSprint,
  getActiveSprint,
  createSprint,
  updateSprint,
  deleteSprint,
  startSprint,
  completeSprint,
  assignTaskToSprint,
  bulkAssignTasksToSprint,
  getSprintTasks,
} from '@/db/sprints';
import type {
  Sprint,
  SprintSummary,
  CreateSprintData,
  UpdateSprintData,
} from '@/db/sprints';

// Epic imports
import {
  getEpicsBySpace,
  getEpicSummaries,
  getEpic,
  createEpic,
  updateEpic,
  deleteEpic,
  assignTaskToEpic,
  bulkAssignTasksToEpic,
  getEpicTasks,
  updateEpicProgress,
} from '@/db/epics';
import type {
  Epic,
  EpicSummary,
  CreateEpicData,
  UpdateEpicData,
} from '@/db/epics';

// Task relations imports
import {
  getTaskRelations,
  getOutgoingRelations,
  getIncomingRelations,
  getBlockingTasks,
  getBlockedTasks,
  getRelatedTasks,
  createRelation,
  deleteRelation,
  deleteRelationByTasks,
  createBlockingRelation,
  createRelatedRelation,
  createDuplicateRelation,
  isTaskBlocked,
  getBlockedTaskIds,
} from '@/db/taskRelations';
import type {
  TaskRelation,
  CreateRelationData,
  RelationType,
} from '@/db/taskRelations';

// Milestone imports
import {
  createMilestone,
  getMilestone,
  getMilestonesBySpace,
  getMilestonesWithProgress,
  getUpcomingMilestones,
  getOverdueMilestones,
  updateMilestone,
  deleteMilestone,
  completeMilestone,
  markMilestoneMissed,
  reorderMilestones,
  assignTaskToMilestone,
  getTasksByMilestone,
  bulkAssignTasksToMilestone,
  getMilestoneStats,
} from '@/db/milestones';
import type {
  Milestone,
  MilestoneWithProgress,
  CreateMilestoneInput,
  UpdateMilestoneInput,
} from '@/db/milestones';

// Automation imports
import {
  getAutomationsBySpace,
  getEnabledAutomations,
  getAutomation,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  toggleAutomation,
  logAutomationExecution,
  getAutomationLogs,
  evaluateConditions,
} from '@/db/automations';
import type {
  Automation,
  AutomationLog,
  CreateAutomationData,
  UpdateAutomationData,
  AutomationCondition,
  AutomationAction,
  TriggerType,
} from '@/db/automations';

// Template imports
import {
  createTemplate,
  getTemplate,
  getTemplatesBySpace,
  getGlobalTemplates,
  getTemplatesByCategory,
  searchTemplates,
  updateTemplate,
  deleteTemplate,
  incrementTemplateUsage,
  duplicateTemplate,
  createCategory,
  getCategoriesBySpace,
  updateCategory,
  deleteCategory,
  generateTaskFromTemplate,
  initializeBuiltinTemplates,
} from '@/db/templates';
import type {
  TaskTemplate,
  TemplateCategory,
  CreateTemplateInput,
  UpdateTemplateInput,
} from '@/db/templates';

// ============================================
// Context value interface
// ============================================

interface ExtendedDataContextValue {
  // State
  loading: boolean;
  error: string | null;

  // Sprint operations
  sprints: {
    getBySpace: (spaceId: string) => Promise<Sprint[]>;
    getSummaries: (spaceId: string) => Promise<SprintSummary[]>;
    getById: (id: string) => Promise<Sprint | null>;
    getActive: (spaceId: string) => Promise<Sprint | null>;
    create: (data: CreateSprintData) => Promise<Sprint>;
    update: (id: string, updates: UpdateSprintData) => Promise<Sprint>;
    delete: (id: string) => Promise<boolean>;
    start: (id: string) => Promise<Sprint>;
    complete: (id: string) => Promise<Sprint>;
    assignTask: (taskId: string, sprintId: string | null) => Promise<void>;
    bulkAssignTasks: (taskIds: string[], sprintId: string | null) => Promise<void>;
    getTasks: (sprintId: string) => Promise<string[]>;
  };

  // Epic operations
  epics: {
    getBySpace: (spaceId: string) => Promise<Epic[]>;
    getSummaries: (spaceId: string) => Promise<EpicSummary[]>;
    getById: (id: string) => Promise<Epic | null>;
    create: (data: CreateEpicData) => Promise<Epic>;
    update: (id: string, updates: UpdateEpicData) => Promise<Epic>;
    delete: (id: string) => Promise<boolean>;
    assignTask: (taskId: string, epicId: string | null) => Promise<void>;
    bulkAssignTasks: (taskIds: string[], epicId: string | null) => Promise<void>;
    getTasks: (epicId: string) => Promise<string[]>;
    updateProgress: (epicId: string) => Promise<Epic>;
  };

  // Task relations operations
  relations: {
    getForTask: (taskId: string) => Promise<TaskRelation[]>;
    getOutgoing: (taskId: string) => Promise<TaskRelation[]>;
    getIncoming: (taskId: string) => Promise<TaskRelation[]>;
    getBlockingTasks: (taskId: string) => Promise<string[]>;
    getBlockedTasks: (taskId: string) => Promise<string[]>;
    getRelatedTasks: (taskId: string) => Promise<string[]>;
    create: (data: CreateRelationData) => Promise<TaskRelation>;
    delete: (id: string) => Promise<boolean>;
    deleteByTasks: (sourceTaskId: string, targetTaskId: string, relationType: RelationType) => Promise<boolean>;
    createBlocking: (blockingTaskId: string, blockedTaskId: string, createdBy?: string) => Promise<TaskRelation>;
    createRelated: (taskId1: string, taskId2: string, createdBy?: string) => Promise<TaskRelation>;
    createDuplicate: (duplicateTaskId: string, originalTaskId: string, createdBy?: string) => Promise<TaskRelation>;
    isBlocked: (taskId: string) => Promise<boolean>;
    getBlockedIds: () => Promise<Set<string>>;
  };

  // Milestone operations
  milestones: {
    create: (input: CreateMilestoneInput) => Promise<Milestone>;
    getById: (id: string) => Promise<Milestone | null>;
    getBySpace: (spaceId: string) => Promise<Milestone[]>;
    getWithProgress: (spaceId: string) => Promise<MilestoneWithProgress[]>;
    getUpcoming: (options: { spaceId?: string; days?: number; limit?: number }) => Promise<MilestoneWithProgress[]>;
    getOverdue: (spaceId?: string) => Promise<MilestoneWithProgress[]>;
    update: (id: string, updates: UpdateMilestoneInput) => Promise<Milestone>;
    delete: (id: string) => Promise<void>;
    complete: (id: string) => Promise<Milestone>;
    markMissed: (id: string) => Promise<Milestone>;
    reorder: (milestoneIds: string[]) => Promise<void>;
    assignTask: (taskId: string, milestoneId: string | null) => Promise<void>;
    getTasks: (milestoneId: string) => Promise<Array<{ id: string; name: string; status: string; dueDate: number | null; assignees: string[] }>>;
    bulkAssignTasks: (taskIds: string[], milestoneId: string | null) => Promise<void>;
    getStats: (spaceId: string) => Promise<{ total: number; open: number; inProgress: number; completed: number; missed: number; overdue: number; upcomingThisWeek: number }>;
  };

  // Automation operations
  automations: {
    getBySpace: (spaceId: string) => Promise<Automation[]>;
    getEnabled: (spaceId: string, triggerType: TriggerType) => Promise<Automation[]>;
    getById: (id: string) => Promise<Automation | null>;
    create: (data: CreateAutomationData) => Promise<Automation>;
    update: (id: string, updates: UpdateAutomationData) => Promise<Automation>;
    delete: (id: string) => Promise<boolean>;
    toggle: (id: string) => Promise<Automation>;
    logExecution: (data: { automationId: string; taskId?: string; triggerData: Record<string, unknown>; actionsExecuted: AutomationAction[]; success: boolean; errorMessage?: string }) => Promise<AutomationLog>;
    getLogs: (automationId: string, limit?: number) => Promise<AutomationLog[]>;
    evaluateConditions: (conditions: AutomationCondition[], task: Record<string, unknown>) => boolean;
  };

  // Template operations
  templates: {
    create: (input: CreateTemplateInput) => Promise<TaskTemplate>;
    getById: (id: string) => Promise<TaskTemplate | null>;
    getBySpace: (spaceId: string) => Promise<TaskTemplate[]>;
    getGlobal: () => Promise<TaskTemplate[]>;
    getByCategory: (categoryId: string) => Promise<TaskTemplate[]>;
    search: (query: string, spaceId?: string) => Promise<TaskTemplate[]>;
    update: (id: string, updates: UpdateTemplateInput) => Promise<TaskTemplate>;
    delete: (id: string) => Promise<void>;
    incrementUsage: (id: string) => Promise<void>;
    duplicate: (id: string, newName?: string, targetSpaceId?: string) => Promise<TaskTemplate>;
    generateTask: typeof generateTaskFromTemplate;
    initializeBuiltins: (spaceId: string) => Promise<TaskTemplate[]>;
    // Category operations
    createCategory: (input: { spaceId?: string; name: string; description?: string; color?: string; icon?: string }) => Promise<TemplateCategory>;
    getCategoriesBySpace: (spaceId: string) => Promise<TemplateCategory[]>;
    updateCategory: (id: string, updates: { name?: string; description?: string; color?: string; icon?: string; orderIndex?: number }) => Promise<TemplateCategory>;
    deleteCategory: (id: string) => Promise<void>;
  };
}

// ============================================
// Context creation with HMR safety
// ============================================

const __global = globalThis as unknown as {
  __ZendaExtendedDataContext?: React.Context<ExtendedDataContextValue | null>;
};
const ExtendedDataContext = __global.__ZendaExtendedDataContext ?? createContext<ExtendedDataContextValue | null>(null);
if (import.meta.env.DEV) {
  __global.__ZendaExtendedDataContext = ExtendedDataContext;
}

// ============================================
// Provider
// ============================================

export function ExtendedDataProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflightCount = useRef(0);

  // Helper to wrap async operations with error handling and loading state
  const withErrorHandling = useCallback(
    async <T,>(operation: () => Promise<T>, fallbackMessage: string): Promise<T> => {
      inflightCount.current += 1;
      if (inflightCount.current === 1) {
        setLoading(true);
      }
      try {
        setError(null);
        return await operation();
      } catch (err) {
        const message = err instanceof Error ? err.message : fallbackMessage;
        setError(message);
        throw err;
      } finally {
        inflightCount.current -= 1;
        if (inflightCount.current === 0) {
          setLoading(false);
        }
      }
    },
    [],
  );

  // Sprint operations
  const sprintOps = useMemo(
    () => ({
      getBySpace: (spaceId: string) =>
        withErrorHandling(() => getSprintsBySpace(spaceId), 'Failed to load sprints'),
      getSummaries: (spaceId: string) =>
        withErrorHandling(() => getSprintSummaries(spaceId), 'Failed to load sprint summaries'),
      getById: (id: string) =>
        withErrorHandling(() => getSprint(id), 'Failed to load sprint'),
      getActive: (spaceId: string) =>
        withErrorHandling(() => getActiveSprint(spaceId), 'Failed to load active sprint'),
      create: (data: CreateSprintData) =>
        withErrorHandling(() => createSprint(data), 'Failed to create sprint'),
      update: (id: string, updates: UpdateSprintData) =>
        withErrorHandling(() => updateSprint(id, updates), 'Failed to update sprint'),
      delete: (id: string) =>
        withErrorHandling(() => deleteSprint(id), 'Failed to delete sprint'),
      start: (id: string) =>
        withErrorHandling(() => startSprint(id), 'Failed to start sprint'),
      complete: (id: string) =>
        withErrorHandling(() => completeSprint(id), 'Failed to complete sprint'),
      assignTask: (taskId: string, sprintId: string | null) =>
        withErrorHandling(() => assignTaskToSprint(taskId, sprintId), 'Failed to assign task to sprint'),
      bulkAssignTasks: (taskIds: string[], sprintId: string | null) =>
        withErrorHandling(() => bulkAssignTasksToSprint(taskIds, sprintId), 'Failed to bulk assign tasks to sprint'),
      getTasks: (sprintId: string) =>
        withErrorHandling(() => getSprintTasks(sprintId), 'Failed to get sprint tasks'),
    }),
    [withErrorHandling],
  );

  // Epic operations
  const epicOps = useMemo(
    () => ({
      getBySpace: (spaceId: string) =>
        withErrorHandling(() => getEpicsBySpace(spaceId), 'Failed to load epics'),
      getSummaries: (spaceId: string) =>
        withErrorHandling(() => getEpicSummaries(spaceId), 'Failed to load epic summaries'),
      getById: (id: string) =>
        withErrorHandling(() => getEpic(id), 'Failed to load epic'),
      create: (data: CreateEpicData) =>
        withErrorHandling(() => createEpic(data), 'Failed to create epic'),
      update: (id: string, updates: UpdateEpicData) =>
        withErrorHandling(() => updateEpic(id, updates), 'Failed to update epic'),
      delete: (id: string) =>
        withErrorHandling(() => deleteEpic(id), 'Failed to delete epic'),
      assignTask: (taskId: string, epicId: string | null) =>
        withErrorHandling(() => assignTaskToEpic(taskId, epicId), 'Failed to assign task to epic'),
      bulkAssignTasks: (taskIds: string[], epicId: string | null) =>
        withErrorHandling(() => bulkAssignTasksToEpic(taskIds, epicId), 'Failed to bulk assign tasks to epic'),
      getTasks: (epicId: string) =>
        withErrorHandling(() => getEpicTasks(epicId), 'Failed to get epic tasks'),
      updateProgress: (epicId: string) =>
        withErrorHandling(() => updateEpicProgress(epicId), 'Failed to update epic progress'),
    }),
    [withErrorHandling],
  );

  // Task relations operations
  const relationOps = useMemo(
    () => ({
      getForTask: (taskId: string) =>
        withErrorHandling(() => getTaskRelations(taskId), 'Failed to load task relations'),
      getOutgoing: (taskId: string) =>
        withErrorHandling(() => getOutgoingRelations(taskId), 'Failed to load outgoing relations'),
      getIncoming: (taskId: string) =>
        withErrorHandling(() => getIncomingRelations(taskId), 'Failed to load incoming relations'),
      getBlockingTasks: (taskId: string) =>
        withErrorHandling(() => getBlockingTasks(taskId), 'Failed to load blocking tasks'),
      getBlockedTasks: (taskId: string) =>
        withErrorHandling(() => getBlockedTasks(taskId), 'Failed to load blocked tasks'),
      getRelatedTasks: (taskId: string) =>
        withErrorHandling(() => getRelatedTasks(taskId), 'Failed to load related tasks'),
      create: (data: CreateRelationData) =>
        withErrorHandling(() => createRelation(data), 'Failed to create relation'),
      delete: (id: string) =>
        withErrorHandling(() => deleteRelation(id), 'Failed to delete relation'),
      deleteByTasks: (sourceTaskId: string, targetTaskId: string, relationType: RelationType) =>
        withErrorHandling(() => deleteRelationByTasks(sourceTaskId, targetTaskId, relationType), 'Failed to delete relation'),
      createBlocking: (blockingTaskId: string, blockedTaskId: string, createdBy?: string) =>
        withErrorHandling(() => createBlockingRelation(blockingTaskId, blockedTaskId, createdBy), 'Failed to create blocking relation'),
      createRelated: (taskId1: string, taskId2: string, createdBy?: string) =>
        withErrorHandling(() => createRelatedRelation(taskId1, taskId2, createdBy), 'Failed to create related relation'),
      createDuplicate: (duplicateTaskId: string, originalTaskId: string, createdBy?: string) =>
        withErrorHandling(() => createDuplicateRelation(duplicateTaskId, originalTaskId, createdBy), 'Failed to create duplicate relation'),
      isBlocked: (taskId: string) =>
        withErrorHandling(() => isTaskBlocked(taskId), 'Failed to check if task is blocked'),
      getBlockedIds: () =>
        withErrorHandling(() => getBlockedTaskIds(), 'Failed to get blocked task IDs'),
    }),
    [withErrorHandling],
  );

  // Milestone operations
  const milestoneOps = useMemo(
    () => ({
      create: (input: CreateMilestoneInput) =>
        withErrorHandling(() => createMilestone(input), 'Failed to create milestone'),
      getById: (id: string) =>
        withErrorHandling(() => getMilestone(id), 'Failed to load milestone'),
      getBySpace: (spaceId: string) =>
        withErrorHandling(() => getMilestonesBySpace(spaceId), 'Failed to load milestones'),
      getWithProgress: (spaceId: string) =>
        withErrorHandling(() => getMilestonesWithProgress(spaceId), 'Failed to load milestones with progress'),
      getUpcoming: (options: { spaceId?: string; days?: number; limit?: number }) =>
        withErrorHandling(() => getUpcomingMilestones(options), 'Failed to load upcoming milestones'),
      getOverdue: (spaceId?: string) =>
        withErrorHandling(() => getOverdueMilestones(spaceId), 'Failed to load overdue milestones'),
      update: (id: string, updates: UpdateMilestoneInput) =>
        withErrorHandling(() => updateMilestone(id, updates), 'Failed to update milestone'),
      delete: (id: string) =>
        withErrorHandling(() => deleteMilestone(id), 'Failed to delete milestone'),
      complete: (id: string) =>
        withErrorHandling(() => completeMilestone(id), 'Failed to complete milestone'),
      markMissed: (id: string) =>
        withErrorHandling(() => markMilestoneMissed(id), 'Failed to mark milestone as missed'),
      reorder: (milestoneIds: string[]) =>
        withErrorHandling(() => reorderMilestones(milestoneIds), 'Failed to reorder milestones'),
      assignTask: (taskId: string, milestoneId: string | null) =>
        withErrorHandling(() => assignTaskToMilestone(taskId, milestoneId), 'Failed to assign task to milestone'),
      getTasks: (milestoneId: string) =>
        withErrorHandling(() => getTasksByMilestone(milestoneId), 'Failed to get milestone tasks'),
      bulkAssignTasks: (taskIds: string[], milestoneId: string | null) =>
        withErrorHandling(() => bulkAssignTasksToMilestone(taskIds, milestoneId), 'Failed to bulk assign tasks to milestone'),
      getStats: (spaceId: string) =>
        withErrorHandling(() => getMilestoneStats(spaceId), 'Failed to get milestone stats'),
    }),
    [withErrorHandling],
  );

  // Automation operations
  const automationOps = useMemo(
    () => ({
      getBySpace: (spaceId: string) =>
        withErrorHandling(() => getAutomationsBySpace(spaceId), 'Failed to load automations'),
      getEnabled: (spaceId: string, triggerType: TriggerType) =>
        withErrorHandling(() => getEnabledAutomations(spaceId, triggerType), 'Failed to load enabled automations'),
      getById: (id: string) =>
        withErrorHandling(() => getAutomation(id), 'Failed to load automation'),
      create: (data: CreateAutomationData) =>
        withErrorHandling(() => createAutomation(data), 'Failed to create automation'),
      update: (id: string, updates: UpdateAutomationData) =>
        withErrorHandling(() => updateAutomation(id, updates), 'Failed to update automation'),
      delete: (id: string) =>
        withErrorHandling(() => deleteAutomation(id), 'Failed to delete automation'),
      toggle: (id: string) =>
        withErrorHandling(() => toggleAutomation(id), 'Failed to toggle automation'),
      logExecution: (data: { automationId: string; taskId?: string; triggerData: Record<string, unknown>; actionsExecuted: AutomationAction[]; success: boolean; errorMessage?: string }) =>
        withErrorHandling(() => logAutomationExecution(data), 'Failed to log automation execution'),
      getLogs: (automationId: string, limit?: number) =>
        withErrorHandling(() => getAutomationLogs(automationId, limit), 'Failed to load automation logs'),
      evaluateConditions,
    }),
    [withErrorHandling],
  );

  // Template operations
  const templateOps = useMemo(
    () => ({
      create: (input: CreateTemplateInput) =>
        withErrorHandling(() => createTemplate(input), 'Failed to create template'),
      getById: (id: string) =>
        withErrorHandling(() => getTemplate(id), 'Failed to load template'),
      getBySpace: (spaceId: string) =>
        withErrorHandling(() => getTemplatesBySpace(spaceId), 'Failed to load templates'),
      getGlobal: () =>
        withErrorHandling(() => getGlobalTemplates(), 'Failed to load global templates'),
      getByCategory: (categoryId: string) =>
        withErrorHandling(() => getTemplatesByCategory(categoryId), 'Failed to load templates by category'),
      search: (query: string, spaceId?: string) =>
        withErrorHandling(() => searchTemplates(query, spaceId), 'Failed to search templates'),
      update: (id: string, updates: UpdateTemplateInput) =>
        withErrorHandling(() => updateTemplate(id, updates), 'Failed to update template'),
      delete: (id: string) =>
        withErrorHandling(() => deleteTemplate(id), 'Failed to delete template'),
      incrementUsage: (id: string) =>
        withErrorHandling(() => incrementTemplateUsage(id), 'Failed to increment template usage'),
      duplicate: (id: string, newName?: string, targetSpaceId?: string) =>
        withErrorHandling(() => duplicateTemplate(id, newName, targetSpaceId), 'Failed to duplicate template'),
      generateTask: generateTaskFromTemplate,
      initializeBuiltins: (spaceId: string) =>
        withErrorHandling(() => initializeBuiltinTemplates(spaceId), 'Failed to initialize builtin templates'),
      // Category operations
      createCategory: (input: { spaceId?: string; name: string; description?: string; color?: string; icon?: string }) =>
        withErrorHandling(() => createCategory(input), 'Failed to create category'),
      getCategoriesBySpace: (spaceId: string) =>
        withErrorHandling(() => getCategoriesBySpace(spaceId), 'Failed to load categories'),
      updateCategory: (id: string, updates: { name?: string; description?: string; color?: string; icon?: string; orderIndex?: number }) =>
        withErrorHandling(() => updateCategory(id, updates), 'Failed to update category'),
      deleteCategory: (id: string) =>
        withErrorHandling(() => deleteCategory(id), 'Failed to delete category'),
    }),
    [withErrorHandling],
  );

  const value: ExtendedDataContextValue = useMemo(
    () => ({
      loading,
      error,
      sprints: sprintOps,
      epics: epicOps,
      relations: relationOps,
      milestones: milestoneOps,
      automations: automationOps,
      templates: templateOps,
    }),
    [loading, error, sprintOps, epicOps, relationOps, milestoneOps, automationOps, templateOps],
  );

  return (
    <ExtendedDataContext.Provider value={value}>
      {children}
    </ExtendedDataContext.Provider>
  );
}

// ============================================
// Hooks
// ============================================

export function useExtendedData() {
  const context = useContext(ExtendedDataContext);
  if (!context) {
    throw new Error('useExtendedData must be used within an ExtendedDataProvider');
  }
  return context;
}

// Convenience hooks for individual domains
export function useSprints() {
  const { sprints, loading, error } = useExtendedData();
  return { ...sprints, loading, error };
}

export function useEpics() {
  const { epics, loading, error } = useExtendedData();
  return { ...epics, loading, error };
}

export function useTaskRelations() {
  const { relations, loading, error } = useExtendedData();
  return { ...relations, loading, error };
}

export function useMilestones() {
  const { milestones, loading, error } = useExtendedData();
  return { ...milestones, loading, error };
}

export function useAutomations() {
  const { automations, loading, error } = useExtendedData();
  return { ...automations, loading, error };
}

export function useTemplates() {
  const { templates, loading, error } = useExtendedData();
  return { ...templates, loading, error };
}
